import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import { useLayoutContext } from "../../layouts/general-layout";
import ChatHeader from "../../components/chat-header";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../utils/userStore";
import Button from "../../components/button";
import { Loader } from "../../assets/images/index";
import { PaymentService } from "../../services/payment/payment.service";
import "./payment.scss";

export default function Index() {
    const { t } = useLayoutContext();
    const { ROUTERS_PATH } = RouterConstant;
    const navigate = useNavigate();
    const { userInfo } = useUserStore();
    const paymentFormRef = useRef<HTMLFormElement>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const paymentService = new PaymentService();
    const location = useLocation();
    const { selectedPackage } = location.state || {};
    const isTrial = selectedPackage?.is_trial;
    const isPrePaid = selectedPackage?.is_pre_paid;

    const [formValues, setFormValues] = useState({
        cardNumber: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: '',
        citizenId: ''
    });

    const [errors, setErrors] = useState({
        cardNumber: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: '',
        citizenId: ''
    });

    const [touched, setTouched] = useState({
        cardNumber: false,
        expirationMonth: false,
        expirationYear: false,
        cvv: false,
        citizenId: false
    });

    const validateIsraeliID = (id: string): boolean => {
        if (!id || id.length !== 9 || !/^\d+$/.test(id)) return false;
        
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            let num = Number(id[i]) * ((i % 2) + 1);
            sum += num > 9 ? num - 9 : num;
        }
        return sum % 10 === 0;
    };

    const validateField = (name: string, value: string): string => {
        switch (name) {
            case 'cardNumber':
                const cleaned = value.replace(/\s/g, '');
                if (!cleaned) return t('card_number_required') || 'Card number is required';
                if (!/^\d{13,19}$/.test(cleaned)) return t('card_number_invalid') || 'Invalid card number';
                return '';
            
            case 'expirationMonth':
                if (!value) return t('month_required') || 'Month is required';
                const month = parseInt(value);
                if (month < 1 || month > 12) return t('month_invalid') || 'Invalid month';
                return '';
            
            case 'expirationYear':
                if (!value) return t('year_required') || 'Year is required';
                const year = parseInt(value);
                const currentYear = new Date().getFullYear();
                if (year < currentYear || year > currentYear + 20) return t('year_invalid') || 'Invalid year';
                return '';
            
            case 'cvv':
                if (!value) return t('cvv_required') || 'CVV is required';
                if (!/^\d{3,4}$/.test(value)) return t('cvv_invalid') || 'CVV must be 3-4 digits';
                return '';
            
            case 'citizenId':
                if (!value) return t('citizen_id_required') || 'Citizen ID is required';
                if (!validateIsraeliID(value)) return t('citizen_id_invalid') || 'Invalid Israeli ID';
                return '';
            
            default:
                return '';
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
        
        if (touched[name as keyof typeof touched]) {
            const error = validateField(name, value);
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const isFormValid = (): boolean => {
        return Object.values(formValues).every(val => val.trim() !== '') &&
               Object.values(errors).every(err => err === '');
    };

    useEffect(() => {
        if (!selectedPackage) return;
        
        if (selectedPackage.is_trial || selectedPackage.is_pre_paid) {
            handleDirectPayment();
            return;
        }        
        const loadScript = (src: string) => {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
            });
        };

        const initPaymentForm = async () => {
            try {
                await loadScript('https://code.jquery.com/jquery-3.6.0.min.js');
                await loadScript('https://app.sumit.co.il/scripts/payments.js');

                // @ts-ignore
                window.jQuery(function() {
                    // @ts-ignore
                        window.OfficeGuy.Payments.BindFormSubmit({
                            FormSelector: '[data-og="form"]',
                            CompanyID: import.meta.env.VITE_PAYMENT_COMPANY_ID,
                            APIPublicKey: import.meta.env.VITE_PAYMENT_API_PUBLIC_KEY,

                            Callback: async function(token: string | null) {                                
                                console.log('Token received:', token);
                                
                                if (!token) {                                    
                                    setIsProcessing(false);
                                     return;
                                }

                                try {
                                    const response = await paymentService.process_payment(selectedPackage, token);
                                    if (response.success) {
                                    useUserStore.getState().setUserInfo({ is_access: true });
                                    }
                                    navigate(ROUTERS_PATH.PAYMENTSTATUS, { state: {response}});

                                } catch (error) {
                                    navigate(ROUTERS_PATH.PAYMENTSTATUS, { state: { success: false } });
                                    setIsProcessing(false);
                                }
                            }
                        });
                        const form = document.querySelector('[data-og="form"]');
                        form?.addEventListener('submit', () => {
                            setIsProcessing(true);
                        });
                });

                
            } catch (error) {
                console.error('Failed to load scripts:', error);
            }
        };

        initPaymentForm();
    }, []);

    const handleBackClick = () => {
        navigate(ROUTERS_PATH.CHOOSEPACKAGES);
    };
    
    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        const allTouched = {
            cardNumber: true,
            expirationMonth: true,
            expirationYear: true,
            cvv: true,
            citizenId: true
        };
        setTouched(allTouched);
        
        const newErrors = {
            cardNumber: validateField('cardNumber', formValues.cardNumber),
            expirationMonth: validateField('expirationMonth', formValues.expirationMonth),
            expirationYear: validateField('expirationYear', formValues.expirationYear),
            cvv: validateField('cvv', formValues.cvv),
            citizenId: validateField('citizenId', formValues.citizenId)
        };
        setErrors(newErrors);
        
        if (Object.values(newErrors).every(err => err === '')) {
            setIsProcessing(true);
            paymentFormRef.current?.submit();
        }
    };

    const handleDirectPayment = async () => {
        setIsProcessing(true);
        try {
            const response = await paymentService.process_payment(selectedPackage, "");
            if (response.success) {
               useUserStore.getState().setUserInfo({ is_access: true });
            }
            navigate(ROUTERS_PATH.PAYMENTSTATUS, { state: {response}});
        } catch (error) {
            navigate(ROUTERS_PATH.PAYMENTSTATUS, { state: { success: false } });
        }
    };    

    if (isTrial || isPrePaid) {
        return (
            <div className="full-template payment-page">
                <ChatHeader chatHeading={t("payment_page")} onBackClick={handleBackClick} />
                <div className="payment-page-body">
                    {isProcessing && (
                        <div className="payment-loader-overlay">
                            <img src={Loader} alt="Loading" className="payment-loader" />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
    <div className="full-template payment-page">
        <ChatHeader chatHeading={t("payment_page")} onBackClick={handleBackClick} />
        <div  className="payment-page-body">
            <form ref={paymentFormRef} data-og="form" className="payment-form" onSubmit={handleFormSubmit}>
                <div className="payment-content">
                    <div className="og-errors"></div>
                    <div className="payment-form-fields">
                        <div className="form-field">
                            <label>
                                <span>{t("CardNumber_title")}</span>
                                <input 
                                    type="text" 
                                    name="cardNumber"
                                    data-og="cardnumber"
                                    value={formValues.cardNumber}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                />
                            </label>
                            {touched.cardNumber && errors.cardNumber && (
                                <span className="field-error">{errors.cardNumber}</span>
                            )}
                        </div>
                        <div className="form-field expiration-field">
                            <label>
                                <span>{t("Card_expiration_title")}</span>
                                <div className="expiration-inputs">
                                    <input 
                                        type="text" 
                                        name="expirationMonth"
                                        data-og="expirationmonth" 
                                        placeholder="MM"
                                        value={formValues.expirationMonth}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        maxLength={2}
                                    />
                                    <span className="separator">/</span>
                                    <input 
                                        type="text" 
                                        name="expirationYear"
                                        data-og="expirationyear" 
                                        placeholder="YYYY"
                                        value={formValues.expirationYear}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        maxLength={4}
                                    />
                                </div>
                            </label>
                            {touched.expirationMonth && errors.expirationMonth && (
                                <span className="field-error">{errors.expirationMonth}</span>
                            )}
                            {touched.expirationYear && errors.expirationYear && (
                                <span className="field-error">{errors.expirationYear}</span>
                            )}
                        </div>
                        <div className="form-field">
                            <label>
                                <span>{t("CVV_title")}</span>
                                <input 
                                    type="text" 
                                    name="cvv"
                                    data-og="cvv"
                                    value={formValues.cvv}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    maxLength={4}
                                />
                            </label>
                            {touched.cvv && errors.cvv && (
                                <span className="field-error">{errors.cvv}</span>
                            )}
                        </div>
                        <div className="form-field">
                            <label>
                                <span>{t("citizenid_title")}</span>
                                <input 
                                    type="text" 
                                    name="citizenId"
                                    data-og="citizenid"
                                    value={formValues.citizenId}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    maxLength={9}
                                />
                            </label>
                            {touched.citizenId && errors.citizenId && (
                                <span className="field-error">{errors.citizenId}</span>
                            )}
                        </div>
                    </div>
                </div>     

            <div className="payment-footer">
                <Button
                    text={t("proceedToPayment")}
                    color="blue" 
                    loader={isProcessing}             
                    disabled={isProcessing || !isFormValid()}
                    type="submit"
                    className="proceed_to_payment_button"
                />
            </div>            
            </form> 
        </div>           
    </div>
);
}
